import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { publicAPI } from '../../services/api';
import toast from 'react-hot-toast';

const PLAN_DETAILS = {
  starter: {
    name: 'Starter',
    trialDays: 14,
    price: '$39/month after trial',
  },
  pro: {
    name: 'Pro',
    trialDays: 14,
    price: '$99/month after trial',
  },
  enterprise: {
    name: 'Enterprise',
    trialDays: 14,
    price: '$500/month after trial',
  },
};

export default function AdminSignup() {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const planFromUrl = searchParams.get('plan') || 'starter';

  // Invite state
  const [inviteData, setInviteData] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(!!inviteToken);
  const [inviteError, setInviteError] = useState(null);

  // Determine plan and trial days based on invite or URL param
  const selectedPlan = inviteData?.plan || planFromUrl;
  const trialDays = inviteData?.trial_days || PLAN_DETAILS[selectedPlan]?.trialDays || 14;
  const planDetails = {
    ...PLAN_DETAILS[selectedPlan],
    trialDays: trialDays,
  };

  const [step, setStep] = useState('signup'); // 'signup' or 'confirm'
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);

  const { signup, confirmSignup } = useAuth();
  const navigate = useNavigate();

  // Validate invite token on mount
  useEffect(() => {
    if (inviteToken) {
      validateInvite();
    }
  }, [inviteToken]);

  const validateInvite = async () => {
    try {
      setInviteLoading(true);
      const response = await publicAPI.validateInviteToken(inviteToken);

      if (response.valid) {
        setInviteData({
          plan: response.plan,
          trial_days: response.trial_days,
          invite_name: response.invite_name,
        });
        // Store invite token for trial initialization
        localStorage.setItem('pending_invite_token', inviteToken);
      } else {
        setInviteError(response.message || 'Invalid invite link');
      }
    } catch (error) {
      console.error('Error validating invite:', error);
      setInviteError('Failed to validate invite link');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const result = await signup(username, email, password, selectedPlan);
      if (result.success) {
        // Store invite token if present (for use during trial initialization)
        if (inviteToken && inviteData) {
          localStorage.setItem('pending_invite_token', inviteToken);
        }
        toast.success('Account created! Please check your email for the confirmation code.');
        setStep('confirm');
      } else {
        toast.error(result.message || 'Signup failed');
      }
    } catch (error) {
      toast.error('Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await confirmSignup(username, confirmationCode);
      if (result.success) {
        toast.success('Account confirmed! You can now sign in.');
        navigate('/admin/login');
      } else {
        toast.error(result.message || 'Confirmation failed');
      }
    } catch (error) {
      toast.error('Confirmation failed');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while validating invite
  if (inviteLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
        style={{
          background: 'linear-gradient(135deg, #E8F5F5 0%, #FFFFFF 50%, #E8F5F5 100%)',
        }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cleona-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-manrope">Validating invite link...</p>
        </div>
      </div>
    );
  }

  // Show error if invite is invalid
  if (inviteToken && inviteError) {
    return (
      <div
        className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
        style={{
          background: 'linear-gradient(135deg, #E8F5F5 0%, #FFFFFF 50%, #E8F5F5 100%)',
        }}
      >
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="font-jakarta text-2xl font-bold text-gray-900 mb-2">Invalid Invite Link</h2>
            <p className="text-gray-600 font-manrope mb-6">{inviteError}</p>
            <Link
              to="/admin/signup"
              className="inline-block rounded-xl bg-cleona-600 px-6 py-3 text-base font-semibold text-white hover:bg-cleona-700 transition-colors font-manrope"
            >
              Continue with Regular Signup
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{
        background: 'linear-gradient(135deg, #E8F5F5 0%, #FFFFFF 50%, #E8F5F5 100%)',
      }}
    >
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          {/* Smart Assistant™ Badge */}
          <div className="inline-flex items-center gap-2 rounded-full bg-cleona-600 px-4 py-2 mb-6">
            <span className="text-white font-manrope font-semibold text-sm">Smart Assistant™</span>
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white">
              <svg className="w-3.5 h-3.5 text-cleona-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </span>
          </div>

          <h1 className="font-jakarta text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            {step === 'signup' ? 'Create Smart Assistant™' : 'Confirm Your Email'}
          </h1>
          <p className="text-gray-600 font-manrope">
            {step === 'signup'
              ? 'Set up a branded assistant for your team in a few minutes.'
              : 'Enter the confirmation code sent to your email'
            }
          </p>

          {/* Invite Info Banner */}
          {step === 'signup' && inviteData && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-3">
              <p className="text-sm text-green-800 font-manrope">
                <span className="font-semibold">Special Invite:</span> {inviteData.invite_name}
              </p>
              <p className="text-sm text-green-700 font-manrope mt-1">
                <span className="font-semibold">{planDetails.name} Plan</span>
                <span className="mx-2">-</span>
                <span>{trialDays}-day extended trial</span>
              </p>
            </div>
          )}

          {/* Plan Info (non-invite) */}
          {step === 'signup' && !inviteData && (
            <div className="mt-4 text-center">
              <div className="inline-flex items-center gap-2 text-sm text-gray-500 font-manrope">
                <span className="font-semibold text-cleona-600">{planDetails.name} Plan</span>
                <span>-</span>
                <span>{planDetails.trialDays}-day free trial</span>
              </div>
              <p className="text-xs text-gray-400 mt-1 font-manrope">
                A payment method will be collected but you won't be charged until your trial ends.
              </p>
            </div>
          )}
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {step === 'signup' ? (
            <form onSubmit={handleSignup} className="space-y-5">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-900 mb-2 font-manrope">
                  User Name
                </label>
                <input
                  id="username"
                  type="text"
                  required
                  placeholder="e.g. Jhon"
                  className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent font-manrope"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2 font-manrope">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  placeholder="example@gmail.com"
                  className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent font-manrope"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-900 mb-2 font-manrope">
                  Password ( min 8 characters, must include a capital letter and a special character )
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  placeholder="New Password"
                  className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent font-manrope"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-900 mb-2 font-manrope">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  placeholder="Confirm New Password"
                  className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent font-manrope"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>

              <div className="space-y-3 pt-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedTerms}
                    onChange={(e) => setAgreedTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-cleona-600 focus:ring-cleona-500"
                  />
                  <span className="text-sm text-gray-600 font-manrope">
                    I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-cleona-600 hover:text-cleona-700 font-medium underline">Terms of Use</a>
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedPrivacy}
                    onChange={(e) => setAgreedPrivacy(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-cleona-600 focus:ring-cleona-500"
                  />
                  <span className="text-sm text-gray-600 font-manrope">
                    I agree to the <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-cleona-600 hover:text-cleona-700 font-medium underline">Privacy Policy</a>
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={loading || !agreedTerms || !agreedPrivacy}
                className="w-full rounded-xl bg-cleona-600 px-6 py-4 text-base font-semibold text-white hover:bg-cleona-700 transition-colors font-manrope disabled:opacity-50 disabled:cursor-not-allowed mt-6"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating account...
                  </span>
                ) : (
                  'Create Account'
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleConfirm} className="space-y-5">
              <div>
                <label htmlFor="confirmationCode" className="block text-sm font-medium text-gray-900 mb-2 font-manrope">
                  Confirmation Code
                </label>
                <input
                  id="confirmationCode"
                  type="text"
                  required
                  placeholder="Enter code from email"
                  className="block w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent font-manrope"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value)}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-cleona-600 px-6 py-4 text-base font-semibold text-white hover:bg-cleona-700 transition-colors font-manrope disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Confirming...
                  </span>
                ) : (
                  'Confirm Account'
                )}
              </button>

              <button
                type="button"
                onClick={() => setStep('signup')}
                className="w-full text-sm text-cleona-600 hover:text-cleona-700 font-manrope"
              >
                ← Back to signup
              </button>
            </form>
          )}
        </div>

        {/* Sign in link */}
        <p className="mt-6 text-center text-gray-600 font-manrope">
          Already have an account?{' '}
          <Link to="/admin/login" className="text-cleona-600 hover:text-cleona-700 font-semibold">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
